<script setup lang="ts">
import { z } from 'zod'
import { fetchGlobalData, fetchInfoData, queryKey } from '~/queries'
import CategoryTemplate from '../pages-templates/CategoryTemplate.vue'
import FaqsSingleTemplate from '../pages-templates/FaqsSingleTemplate.vue'
import FaqsTemplate from '../pages-templates/FaqsTemplate.vue'
import GiftCardProductTemplate from '../pages-templates/GiftCardProductTemplate.vue'
import HtmlContentTemplate from '../pages-templates/HtmlContentTemplate.vue'
import ProductTemplate from '../pages-templates/ProductTemplate.vue'
import FootballClubTemplate from '~/pages-templates/FootballClubTemplate.vue'
import FootballProductTemplate from '~/pages-templates/FootballProductTemplate.vue'
import {useJsonLdStore} from "~/stores/jsonld";
import { useHreflang } from '~/composables/useHreflang'

const route = useRoute()
const { t } = useI18n()

definePageMeta({
  validate: (route) => {
    if (route.params.path && (route.params.path[0] === '_nuxt' || route.params.path[0] === '') ) {
      return false
    }
    return route.params.path && route.params.path.length > 0
  }
})

const path = computed(() => {
  return cleanPath(
    Array.isArray(route.params.path)
      ? route.params.path.join('/')
      : route.params.path
  )
})

const templatesMap = {
  category: CategoryTemplate,
  faqs: FaqsTemplate,
  faqsSingle: FaqsSingleTemplate,
  htmlContent: HtmlContentTemplate,
  product: ProductTemplate,
  productGiftCard: GiftCardProductTemplate,
  categoryFootballClub: FootballClubTemplate,
  productFootball: FootballProductTemplate
}

const supportedTemplates = [
  'category',
  'categoryFootballClub',
  'faqs',
  'faqsSingle',
  'htmlContent',
  'product',
  'productGiftCard',
  'productFootball'
] as const

const isFaqs = computed(() => path.value === 'faqs')
const isFaqsSingle = computed(() => path.value.startsWith('faqs/'))

const shouldFetchInfo = computed(() => !(isFaqs.value || isFaqsSingle.value))

const currentPage = computed(() => {
  const p = parseInt(route.query.page as string)
  return isNaN(p) || p < 2 ? null : p
})
const pageTranslation = t("string.Page")

const authToken = useCookie('authToken')
const { domain } = useSite().site.value

const infoQuery = await useAsyncData(
  queryKey('info', domain, path.value),
  async () => {
    if (!shouldFetchInfo.value) return

    if (!domain || !path.value) {
      throw createError({
        statusCode: 500,
        statusMessage: 'Internal server error',
        data: {
          reason: `Missing required page lookup input (domain: ${domain ? 'present' : 'missing'}, path: ${path.value ? 'present' : 'missing'})`,
        },
      })
    }

    return await fetchInfoData({ domain, path: path.value })
  },
  { watch: [authToken] }
)

await useAsyncData(queryKey('global-data'), fetchGlobalData)

const { data: infoData, refresh, error } = infoQuery

if (error.value) {
  if (error.value.statusCode === 404) {
    throw createError({
      statusCode: 404,
      statusMessage: 'Page not found'
    })
  }

  const reason =
    (error.value.data as { reason?: string } | undefined)?.reason ||
    error.value.statusMessage ||
    error.value.message ||
    'Internal server error'

  throw createError({
    statusCode: 500,
    statusMessage: 'Internal server error',
    data: { reason },
    cause: error.value,
  })
}

const templateName = computed(() => {
  if (isFaqs.value || isFaqsSingle.value) {
    return isFaqsSingle.value ? 'faqsSingle' : 'faqs'
  }

  if (infoData.value?.product) {
    if (infoData.value?.product.type === 'football_match') {
      return 'productFootball'
    }
    if (infoData.value?.product.type === 'gift_card') {
      return 'productGiftCard'
    }
    return 'product'
  }

  let templateName = infoData.value?.page?.templateAlias || ''
  if (templateName === 'category_football_club' || templateName === 'predefined_football_search_page') {
    templateName = 'categoryFootballClub'
  } else if (templateName === 'predefined_search_page') {
    templateName = 'category'
  } else if (['static_page'].includes(templateName)) {
    templateName = 'htmlContent'
  }

  return templateName
})

const template = z.enum(supportedTemplates).safeParse(templateName.value)

if (!template.success) {
  throw createError({
    statusCode: 500,
    statusMessage: 'Template not found'
  })
}

const templateComponent = templatesMap[template.data]

const meta = computed(() => {
  const baseTitle =
    infoData.value?.product?.seoTitle ||
    infoData.value?.product?.title ||
    infoData.value?.page?.seoTitle ||
    infoData.value?.page?.title

  const baseDescription =
    infoData.value?.product?.seoDescription ||
    infoData.value?.page?.seoDescription

  const keywords =
    infoData.value?.product?.seoKeywords || infoData.value?.page?.seoKeywords

  const image = infoData.value?.product?.image || infoData.value?.page?.image

  const ogImage = image ?? undefined

  const canonical = getCanonicalLink(path.value)

  const title = currentPage.value
    ? `${baseTitle} | ${pageTranslation} ${currentPage.value}`
    : baseTitle

  const description = currentPage.value
    ? `${baseDescription} (${pageTranslation} ${currentPage.value})`
    : baseDescription

  return { title, description, keywords, ogImage, canonical }
})

if (!import.meta.client) {
  const jsonStore = useJsonLdStore()
  useJsonld(() => {
    const jsonLd = jsonStore.jsonLd
    return Array.isArray(jsonLd) && jsonLd.length ? jsonLd : undefined
  });
}

useHead({
  link: [{ rel: 'canonical', href: meta.value.canonical }]
})

useSeoMeta({
  title: meta.value.title,
  description: meta.value.description,
  keywords: meta.value.keywords,
  ogDescription: meta.value.description,
  ogUrl: meta.value.canonical,
  ogImage: meta.value.ogImage
})

useHreflang()
</script>

<template>
  <NuxtLayout>
    <component
      :is="templateComponent"
      :key="path"
      :breadcrumbs="[
        { title: $t('string.Home'), path: '/' },
        ...(infoData?.breadcrumbs ?? []),
      ]"
      :page="infoData?.page"
      :product="infoData?.product"
      :refresh="refresh"
    />
  </NuxtLayout>
</template>
